import { BaseMenuItem } from '@ui/menus/Menu/BaseMenuItem';
import { Label } from '@ui/Label';
import { Menu, useMenuContext } from '@ui/menus/Menu';
import { Icon } from '@ui/Icon';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { PROJECT_LABEL_COLORS } from '@shared/models/project-label.model';
import { useProjectsForOverview } from '@client/store/project';
import { usePromiseSnackbar } from '@client/hooks/use-snackbar.hooks';
import {
  assignProjectLabels,
  createProjectLabel,
  useProjectLabelFilter,
  useProjectLabels,
} from './project-labels.hooks';

type ProjectLabelMenuContentProps = {
  projectId?: string;
  readonly?: boolean;
};

export const ProjectLabelPicker = ({
  anchor,
  onClose,
  projectId,
  readonly = false,
}: ProjectLabelMenuContentProps & {
  anchor: HTMLElement | null;
  onClose: () => void;
}) => (
  <Menu
    open={!!anchor}
    anchorEl={anchor}
    onClose={onClose}
    width={280}
    searchable={projectId ? 'Change or add labels…' : 'Filter labels…'}
  >
    <ProjectLabelMenuContent projectId={projectId} readonly={readonly} />
  </Menu>
);

export const ProjectLabelsSubmenu = ({
  projectId,
  readonly = false,
}: {
  projectId: string;
  readonly?: boolean;
}) => (
  <Menu.Submenu
    label="Labels"
    icon={<Icon category="mui" icon="LabelOutlined" />}
    menuWidth={280}
    searchable="Change or add labels…"
  >
    <ProjectLabelMenuContent projectId={projectId} readonly={readonly} />
  </Menu.Submenu>
);

const ProjectLabelMenuContent = ({
  projectId,
  readonly = false,
}: ProjectLabelMenuContentProps) => {
  const { searchString: search, closeAllMenus } = useMenuContext();
  const { labels, assignments, organization } = useProjectLabels();
  const projects = useProjectsForOverview();
  const labelCount = (id: string) =>
    projects.filter((project) => assignments[project.id]?.includes(id)).length;
  const [filter, setFilter] = useProjectLabelFilter();
  const [saving, setSaving] = useState(false);
  const promiseSnackbar = usePromiseSnackbar();
  const selected = projectId
    ? (assignments[projectId] ?? [])
    : filter.split(',').filter(Boolean);
  const name = search.trim();
  const canCreate =
    !!projectId &&
    !readonly &&
    !!name &&
    name.length <= 40 &&
    !labels.some((label) => label.name.toLowerCase() === name.toLowerCase());

  const save = async (ids: string[]) => {
    if (!projectId) {
      setFilter(ids.join(','));
      return;
    }
    await assignProjectLabels(organization, projectId, ids);
  };
  const toggle = (id: string) => {
    setSaving(true);
    void promiseSnackbar(() =>
      save(
        selected.includes(id)
          ? selected.filter((value) => value !== id)
          : [...selected, id],
      ),
    ).finally(() => setSaving(false));
  };
  const createLabel = (color: string) => {
    setSaving(true);
    void promiseSnackbar(async () => {
      const label = await createProjectLabel(organization, {
        name,
        color:
          PROJECT_LABEL_COLORS.find((value) => value === color) ??
          PROJECT_LABEL_COLORS[0],
      });
      await save([...selected, label.id]);
      closeAllMenus();
    }).finally(() => setSaving(false));
  };

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
        {labels.map((label) => (
          <Menu.CheckboxItem
            key={label.id}
            searchText={label.name}
            disabled={saving || (!!projectId && readonly)}
            onChange={() => toggle(label.id)}
            checked={selected.includes(label.id)}
            uncheckedIcon={
              <Box
                sx={{
                  bgcolor: label.color,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                }}
              />
            }
          >
            <Box component="span" sx={{ textTransform: 'uppercase' }}>
              {label.name}
            </Box>
            {!projectId && (
              <Menu.Item.RightIcon>
                <ProjectLabelCount count={labelCount(label.id)} />
              </Menu.Item.RightIcon>
            )}
          </Menu.CheckboxItem>
        ))}
        {!labels.length && !canCreate && (
          <Typography sx={{ p: 1, fontSize: 12, color: 'text.secondary' }}>
            {labels.length ? 'No matching labels' : 'No labels yet'}
          </Typography>
        )}
      </Box>
      {canCreate && (
        <Menu.Submenu
          label={`Create new label: "${name}"`}
          searchText={name}
          icon={<Icon category="mui" icon="Add" />}
          disabled={saving}
          menuWidth={240}
        >
          {PROJECT_LABEL_COLORS.map((value, index) => (
            <BaseMenuItem
              key={value}
              disabled={saving}
              closeOnClick={false}
              onClick={() => createLabel(value)}
              icon={
                <Box
                  sx={{
                    bgcolor: value,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                  }}
                />
              }
            >
              {
                ['Mint', 'Blue', 'Purple', 'Pink', 'Peach', 'Yellow', 'Gray'][
                  index
                ]
              }
            </BaseMenuItem>
          ))}
        </Menu.Submenu>
      )}
    </Box>
  );
};

const ProjectLabelCount = ({ count }: { count: number }) => (
  <Typography
    component="span"
    sx={{ fontSize: 12, whiteSpace: 'nowrap', color: 'text.secondary', pr: 1 }}
  >
    {count}
  </Typography>
);

export const ProjectLabelPills = ({
  projectId,
  onClick,
}: {
  projectId: string;
  onClick: (anchor: HTMLElement) => void;
}) => {
  const { labels, assignments } = useProjectLabels();
  const selected = labels.filter((label) =>
    assignments[projectId]?.includes(label.id),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({
    width: 0,
    visible: selected.length,
    counterWidth: 0,
  });
  const measurementKey = selected.map((label) => label.name).join(',');
  useLayoutEffect(() => {
    const container = containerRef.current;
    const measurement = measurementRef.current;
    if (!container || !measurement) return;
    const measure = () => {
      const children = Array.from(measurement.children);
      const widths = children
        .slice(0, -1)
        .map((child) => child.getBoundingClientRect().width);
      const gap = parseFloat(getComputedStyle(measurement).gap) || 0;
      const width =
        widths.reduce((sum, value) => sum + value, 0) +
        Math.max(0, widths.length - 1) * gap;
      const available = container.getBoundingClientRect().width;
      const counterWidth = children.at(-1)?.getBoundingClientRect().width ?? 0;
      container.parentElement?.style.setProperty(
        '--project-label-counter-width',
        `${counterWidth}px`,
      );
      let visible = widths.length;
      if (width > available) {
        visible = 0;
        let used = counterWidth;
        for (const value of widths) {
          if (used + gap + value > available) break;
          used += gap + value;
          visible++;
        }
      }
      setLayout((previous) =>
        previous.width === width &&
        previous.visible === visible &&
        previous.counterWidth === counterWidth
          ? previous
          : { width, visible, counterWidth },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(measurement);
    return () => observer.disconnect();
  }, [measurementKey]);
  if (!selected.length) return null;
  const openPicker = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    // Individual pills/counters can unmount when hover or focus changes the row width.
    onClick(containerRef.current ?? event.currentTarget);
  };
  const isTruncated = layout.visible < selected.length;
  const tooltipTitle = isTruncated
    ? `Labels: ${selected.map((label) => label.name).join(', ')}`
    : '';
  return (
    <Tooltip title={tooltipTitle}>
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          gap: 0.5,
          minWidth: layout.counterWidth,
          width: layout.width || 'max-content',
          flexShrink: 1,
          ml: 0.5,
          position: 'relative',
        }}
      >
        <Box
          ref={measurementRef}
          aria-hidden
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            display: 'flex',
            gap: 0.5,
            width: 'max-content',
          }}
        >
          {selected.map((label) => (
            <Label key={label.id} label={label.name} sx={{ flexShrink: 0 }} />
          ))}
          <Label
            label={`+${selected.length}`}
            sx={{ '&&': { border: 'none' }, flexShrink: 0 }}
          />
        </Box>
        {selected.slice(0, layout.visible).map((label) => (
          <Label
            key={label.id}
            label={label.name}
            onClick={openPicker}
            onDoubleClick={(event) => event.stopPropagation()}
            borderColor={label.color}
            sx={{ flexShrink: 0 }}
          />
        ))}
        {selected.length > layout.visible && (
          <Label
            label={`${layout.visible === 0 ? '' : '+'}${selected.length - layout.visible}`}
            onClick={openPicker}
            sx={{
              ...(layout.visible > 0 && { '&&': { border: 'none' } }),
              flexShrink: 0,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

/** Existing label filter options, embedded in the combined project filters panel. */
export const ProjectLabelFilterOptions = () => <ProjectLabelMenuContent />;
